/**
 * CandidatesSection (Slice 3C of #860, refactored to hints-UI
 * layout post-#877).
 *
 * Two-panel list/detail layout inside the Candidates tab —
 * mirrors `pages/Hints.tsx`'s `ResizablePanels` structure so the
 * shape feels native next to the existing managed-corpus surface.
 * Left: filter bar + scrollable list. Right: detail of the selected
 * candidate + action buttons (Edit / Re-propose / Archive). The edit
 * dialog owns content edits (name, description, steps, preconditions,
 * tags); the detail panel edits priority + test type inline, saving on
 * change (#2126) — the one narrow exception to the panel's otherwise
 * review-only role.
 *
 * Why not a full page (`pages/Candidates.tsx`) like Hints itself
 * has: the earlier section-vs-page sign-off picked inline so the
 * upload→extract→browse workflow stays on one screen. This file
 * is the hints layout SHAPE applied inline.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { ArrowUpRight, ChevronDown, ChevronRight, Info, Plus, Pencil, Archive, RefreshCw, RotateCcw, Sparkles, Trash2, X } from 'lucide-react'
import {
  Badge,
  Button,
  Label,
  Select,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Tooltip,
} from '@/components/ui'
import { SearchInput } from '@/components/SearchInput'
import { ResizablePanels } from '@/components/ResizablePanels'
import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog'
import { DeleteAllIcon } from '@/components/DeleteAllIcon'
import { useConfirm } from '@/hooks/useConfirm'
import { useToast } from '@/hooks/useToast'
import { applicationsAPI, CandidateInUseError } from '@/lib/api'
import { normalizeIntent } from '@/lib/intent'
import { PRIORITY_BADGE } from '@/lib/priorityBadge'
import { INTENT_BADGE, INTENT_NEUTRAL } from '@/lib/intentBadge'
import { formatDateTime } from '@/lib/utils'
import { triggerRun } from '@/lib/api'
import { useMaxTestCasesPerRun } from '@/hooks/useRunLimits'
import { useManualRunTemplates } from '@/hooks/useManualRunTemplates'
import { PrioritySelect } from './PrioritySelect'
import { IntentSelect } from './IntentSelect'
import {
  CANDIDATE_TEST_TAGS,
  type CandidateTest,
  type CandidateScriptedStatusResponse,
  type ProposedTestTotals,
  type CandidateScriptedTier,
  type CandidateTestTag,
  type ScriptedLink,
  type TestCasePriority,
  type TestIntent,
} from '@/types'
import { CandidateEditDialog } from './CandidateEditDialog'
import { TestAuthoringTemplateDialog } from './TestAuthoringTemplateDialog'
// The coverage strip's tooltip, kept beside the shared pill colors.
import { TEST_COVERAGE_TOOLTIP } from '@/lib/testCoverage'

interface Props {
  appId: string
  /** The app's name. "Delete all proposed tests" asks for it to be typed,
   * the same phrase "Delete all Tests" uses. Without it the action is
   * not offered. */
  appName?: string
  /** The reference-documents manager (and Jira import) to embed behind
   * the "Manage sources" control in the row-1 toolbar (#2261). When
   * omitted, the toolbar shows no sources control — CandidatesSection is
   * only mounted on the Tests page, which always passes this. */
  sourcesPanel?: ReactNode
  /** Reference docs on the parent Application — used for the per-row
   * "from <filename>" provenance label + the per-row re-propose
   * affordance (we only show "Re-propose" when the candidate's
   * referenceDocId resolves to a doc that still exists). */
  referenceDocs?: ReadonlyArray<{
    id: string
    filename: string
    /** The document's parts, for naming the part filter's chip. */
    parts?: ReadonlyArray<{ id: string; title: string; quote?: string }>
  }>
  /** Monotonically-increasing key bumped by the parent
   * (`Applications.tsx`) when a reference-doc extraction transitions
   * non-terminal → terminal (#945 follow-up). A change here triggers
   * a re-fetch of the candidates list so newly-landed proposer output
   * appears without the user having to switch tabs and back. Defaults
   * to 0 from the parent on first render; the initial mount already
   * loads via the existing effect, so a 0 → 0 no-op render doesn't
   * double-fetch. */
  refreshKey?: number
  /** Fired after any mutation that could shift the active-candidate
   * count for this app (create / archive / hard-delete / re-propose
   * — anything that calls `loadCandidates()` post-success). The
   * parent (`Applications.tsx`) wires this to a per-app refresh so
   * the Candidate-tests tab badge (which reads
   * `app.candidateTestCount` cached on the Application doc) stays
   * consistent with the list this panel is showing. Without the
   * callback, the badge would lie until a full app re-fetch — e.g.
   * archiving a candidate would drop it from the list but leave the
   * badge count one too high.
   * Copilot review on PR #972. */
  onCandidatesMutated?: () => void
  /** Show only the suggestions that came from ONE reference document
   * (#2096). Set by the Tests page from its `?referenceDocId=` param,
   * which the reference-doc row's View link carries. Null / undefined
   * shows everything.
   *
   * A separate axis from `filterSource`, which selects a source TYPE
   * (proposed / user-created / imported issue) and cannot isolate a
   * single document. The two compose. */
  referenceDocId?: string | null
  /** Clears the per-document filter. The Tests page owns the URL
   * param, so removing the chip means dropping the param rather than
   * mutating state here — that keeps a reload honest and the filter
   * shareable. */
  onClearReferenceDocFilter?: () => void
  /** Show only one requirement's tests: the same filter a dashboard gap
   * row opens (`?referenceDocId=` and `?partId=`). Offered from a test's
   * Requirement box. */
  onFilterByPart?: (referenceDocId: string, partId: string) => void
  /** Narrow the document filter to ONE part of that document: one
   * requirement or defect, arriving from a dashboard gap row. Cleared
   * with the document filter. */
  partId?: string | null
  /** Show only the proposed tests of ONE imported issue, by the connection
   * it came through and its id. Arrives from a dashboard gap row. */
  issueFilter?: { connectionId: string; externalId: string } | null
  /** Clears `issueFilter`; the Tests page owns the URL params. */
  onClearIssueFilter?: () => void
  /** Open the Sources panel on arrival, for the coverage tile's link to
   * "Mark a document as Requirements". */
  openSourcesOnMount?: boolean
  /** #974 Slice 2 — every variant on the parent Application. Drives
   * the variant-selector dropdown at the top of this section.
   * Optional because some Application views may not have loaded
   * variants yet; the selector hides itself if length ≤ 1. */
  variants?: ReadonlyArray<{ id: string; name: string }>
  /** Currently-selected variant id. The Candidates view is
   * variant-scoped — the (future PR 3) per-row scripted-status
   * badge, the (this PR) tab-badge verified count, and the list
   * summary line all scope to this variant. Owned by the parent so
   * the tab badge in `Applications.tsx` sees the same value the
   * selector below shows. */
  selectedVariantId?: string
  /** Called when the operator picks a different variant in the
   * selector. Parent persists per-app via localStorage + triggers
   * the re-fetch that updates the tab badge. */
  onVariantChange?: (variantId: string) => void
  /** #974 Slice 3 — per-row scripted-status data, scoped to the
   * currently selected variant. Owned by the parent
   * (`Applications.tsx`'s scriptedStatusByApp cache) and passed
   * down so this section + the tab badge consume the same snapshot
   * with no double-fetch. `undefined` during the cold-fetch window
   * (selector just changed, response in flight) — every row falls
   * back to the unstamped / no-badge state, summary suppresses
   * per-tier counts, and the cold state matches the tab badge's
   * stale-snapshot guard from PR #981. Once the response lands AND
   * its variantId matches the current selection, badges + summary
   * fill in. */
  scriptedStatus?: CandidateScriptedStatusResponse
  /** The proposed-test totals behind the toolbar's coverage bar, from the
   * one coverage read the dashboard uses too. The page passes them only
   * when they belong to the selected variant; `undefined` hides the bar. */
  proposedTests?: ProposedTestTotals
  /** Suggestions a generation run is currently writing (#2260).
   *
   * The page owns this because the page owns the run lifecycle: it polls
   * the run and clears these when it ends. The section cannot work it out
   * from `scriptedStatus`, because for the several minutes a run takes
   * the suggestion genuinely has no linked test and the status correctly
   * reads unbuilt. */
  writingCandidateIds?: ReadonlySet<string>
  /** Told when this section starts a generation run, so the page can
   * follow it. Carries the suggestions it was started for. */
  onRunStarted?: (runId: string, candidateIds: string[]) => void
  /** "Show them" on the summary line's uncounted-tests sentence. The
   * page owns the Active tab, so it switches there and narrows the list
   * to `scriptedStatus.uncountedTestIds`. The sentence has no link
   * without this. */
  onShowUncounted?: () => void
  /** A suggestion to open, from a test's Suggestion chip on the Active
   * tab. The section selects it, widening the Active / Archived filter
   * when the suggestion is archived, then calls `onFocusCandidateHandled`
   * so the page can drop the request. */
  focusCandidateId?: string | null
  onFocusCandidateHandled?: () => void
}

/** Per-tier visual style. Chip-with-dot pattern. Colors follow the
 * 4-tier maturity ladder: cool/quiet at the start (proposed: muted)
 * → blue (planned: adopted-but-not-coded) → amber (scripted: code
 * exists but unproven) → green (ready: proven). The two end tiers
 * use semantic tokens for muted/success-ish reads; the middle two
 * use concrete blue/amber because the design system reserves
 * semantic accent tokens for destructive/primary. */
const TIER_BADGE: Record<CandidateScriptedTier, string> = {
  proposed: 'bg-muted text-muted-foreground border-border',
  planned:
    'bg-blue-50 text-blue-700 border-blue-200 ' +
    'dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-900',
  scripted:
    'bg-amber-50 text-amber-700 border-amber-200 ' +
    'dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-900',
  ready:
    'bg-green-50 text-green-700 border-green-200 ' +
    'dark:bg-green-950/30 dark:text-green-400 dark:border-green-900',
}
const TIER_DOT: Record<CandidateScriptedTier, string> = {
  proposed: 'bg-muted-foreground/60',
  planned: 'bg-blue-500 dark:bg-blue-400',
  scripted: 'bg-amber-500 dark:bg-amber-400',
  ready: 'bg-green-600 dark:bg-green-400',
}
const TIER_LABEL: Record<CandidateScriptedTier, string> = {
  proposed: 'Proposed',
  planned: 'Planned',
  scripted: 'Scripted',
  ready: 'Ready',
}

/** The badge for a suggestion a test was built from, after a re-read of
 * its document or issue no longer produced it. Null when it does not
 * apply. A deleted source has its own "document deleted" badge instead. */
function sourceMissingBadge(c: CandidateTest): { label: string; title: string } | null {
  if (!c.missingFromSourceAt || c.wasOrphaned) return null
  const source = c.linkedProvider ? 'issue' : 'document'
  const what =
    `Reading the ${source} again on ${formatDateTime(c.missingFromSourceAt, 'short')} ` +
    "didn't produce this proposed test."
  return {
    label: `No longer in the ${source}`,
    // Coverage counts active suggestions only, so the advice is for those.
    title: c.active ? `${what} Its tests still count. Archive it if it no longer applies.` : what,
  }
}

/** Status-filter dropdown options for the Slice 3 Status filter,
 * updated to the Slice 4 4-tier scheme. `''` is the show-all
 * sentinel — matches the existing TAG_OPTIONS / STATUS_OPTIONS
 * shape. (The source filter is dynamic chips since #1827 — see
 * `distinctCandidateSources` — no longer a fixed options list.) */
const TIER_FILTER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Status' },
  { value: 'proposed', label: 'Proposed' },
  { value: 'planned', label: 'Planned' },
  { value: 'scripted', label: 'Scripted' },
  { value: 'ready', label: 'Ready' },
]

/** #1827 — user-facing metadata for each candidate SOURCE. Both the
 * dynamic source filter AND the per-row provenance chip read this one
 * map, so adding a new source (a future issue-tracker integration,
 * say) is a single entry here — no change to the filter UI or the row
 * layout. An unknown / future `source` value the data carries but the
 * map hasn't been taught falls through to a neutral title-cased
 * entry, so the surface never breaks on data it hasn't seen. */
interface SourceMeta {
  /** Plain-language name shown in the filter + provenance tooltip. */
  label: string
  /** 1-char mark rendered in the small colored square. */
  mark: string
  /** Tailwind background for the mark square. */
  markClass: string
}
const SOURCE_META: Record<string, SourceMeta> = {
  // 'proposed' rows were extracted from a reference document.
  proposed: { label: 'Reference docs', mark: 'D', markClass: 'bg-slate-500' },
  // 'user-created' rows were added by hand on this screen.
  'user-created': { label: 'Added manually', mark: 'M', markClass: 'bg-primary' },
  // #1558 Slice 2 — the Proposer created these from an imported Jira issue.
  // Keyed on the candidate's `linkedProvider`, not its `source` (which
  // stays 'proposed') — see `effectiveSource`.
  jira: { label: 'Jira', mark: 'J', markClass: 'bg-[#0052cc]' },
}

/** The candidate's EFFECTIVE source key for the filter + provenance. A
 * candidate the Proposer made from an external issue keeps `source:
 * 'proposed'` but carries `linkedProvider`; surface it under its provider
 * (e.g. 'jira') so the filter chip + provenance distinguish it from
 * reference-doc candidates (#1558 Slice 2). */
function effectiveSource(c: { source: string; linkedProvider?: string }): string {
  return c.linkedProvider ? c.linkedProvider : c.source
}
function sourceMeta(key: string): SourceMeta {
  return (
    SOURCE_META[key] ?? {
      label: key.charAt(0).toUpperCase() + key.slice(1),
      mark: (key[0] ?? '?').toUpperCase(),
      markClass: 'bg-muted-foreground',
    }
  )
}

/** The distinct candidate sources actually present in a list, in a
 * stable order (the known sources first, then any novel keys as they
 * appear in the data). This is what makes the source filter dynamic —
 * it is derived from the rows on screen, never a hardcoded list
 * (#1827 AC1), so a new source shows up as a filter chip the moment
 * its first candidate lands. Exported for unit tests. */
export function distinctCandidateSources(
  candidates: ReadonlyArray<{ source: string; linkedProvider?: string }>,
): string[] {
  const present = new Set(candidates.map(effectiveSource))
  const ordered: string[] = []
  for (const key of Object.keys(SOURCE_META)) {
    if (present.has(key)) ordered.push(key)
  }
  for (const key of present) {
    if (!ordered.includes(key)) ordered.push(key)
  }
  return ordered
}

/** Provenance shown on each Proposed row: which source produced the
 * candidate + its human identifier within that source (a reference-
 * doc filename today; a future source's own id when one lands). The
 * source itself is carried by the mark; the text is the identifier so
 * the row stays compact. #1827 AC1. Exported for unit tests. */
export interface CandidateProvenance {
  sourceKey: string
  sourceLabel: string
  mark: string
  markClass: string
  /** Per-row identifier within the source (reference-doc filename).
   * Absent for hand-added rows, which have no external id. */
  identifier?: string
}
export function candidateProvenance(
  c: {
    source: string
    referenceDocId?: string | null
    wasOrphaned?: boolean
    linkedProvider?: string
    linkedDisplayKey?: string
  },
  docFilenameById: Map<string, string>,
): CandidateProvenance {
  const key = effectiveSource(c)
  const meta = sourceMeta(key)
  const base = {
    sourceKey: key,
    sourceLabel: meta.label,
    mark: meta.mark,
    markClass: meta.markClass,
  }
  // External-issue provenance — the issue's display key is the identifier.
  if (c.linkedProvider) {
    return { ...base, identifier: c.linkedDisplayKey }
  }
  if (c.source === 'proposed') {
    const filename = c.referenceDocId ? docFilenameById.get(c.referenceDocId) : undefined
    if (filename) return { ...base, identifier: filename }
    // The source doc was deleted or can't be resolved — say so plainly
    // rather than showing a dangling id. Matches the detail panel's
    // "The document this came from was deleted" wording.
    return { ...base, identifier: c.wasOrphaned ? 'Deleted document' : undefined }
  }
  return base
}

/** Compact "source mark + identifier" chip. Reusable so every source
 * renders the same shape and a new source needs no new chip (#1827).
 * The mark square conveys the source; the text is the identifier (or
 * the source label when the source has no per-row id). */
export function ProvenanceChip({ prov }: { prov: CandidateProvenance }) {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
      title={prov.identifier ? `${prov.sourceLabel} · ${prov.identifier}` : prov.sourceLabel}
    >
      <span
        className={`inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] text-[7px] font-bold text-white ${prov.markClass}`}
        aria-hidden="true"
      >
        {prov.mark}
      </span>
      <span className="truncate">{prov.identifier ?? prov.sourceLabel}</span>
    </span>
  )
}

const TAG_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'all', label: 'All tags' },
  ...CANDIDATE_TEST_TAGS.map(t => ({ value: t, label: t })),
]

/** #2160 — list order. Labels carry the "Sort:" prefix so the control reads
 * as an order and not another filter, matching the Active list's dropdown.
 * `priority` first because it is the default and the order the server
 * already returns. */
const SORT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'priority', label: 'Sort: Priority' },
  { value: 'newest', label: 'Sort: Newest' },
  { value: 'name', label: 'Sort: Name' },
]

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
]

/** Intent badge that folds a legacy `validation` / `error-handling`
 * value to Negative on display (#2102) — so an old row reads as the kind
 * it now is, and an unrecognized value shows neutral rather than
 * untinted. The stored value is untouched. */
function IntentBadge({ intent, className = '' }: { intent: string; className?: string }) {
  const norm = normalizeIntent(intent)
  return (
    <Badge variant="outline" className={`${className} ${norm ? INTENT_BADGE[norm] : INTENT_NEUTRAL}`}>
      {norm ?? intent}
    </Badge>
  )
}

export function CandidatesSection({
  appId,
  sourcesPanel,
  referenceDocs,
  referenceDocId,
  onClearReferenceDocFilter,
  onFilterByPart,
  partId,
  issueFilter,
  onClearIssueFilter,
  appName,
  openSourcesOnMount = false,
  refreshKey = 0,
  onCandidatesMutated,
  writingCandidateIds,
  onRunStarted,
  variants,
  selectedVariantId,
  onVariantChange,
  scriptedStatus,
  proposedTests,
  onShowUncounted,
  focusCandidateId,
  onFocusCandidateHandled,
}: Props) {
  // Absolute jumps out of this section are built with the shared
  // workspace-path helper, which reads the active workspace from app
  // context.
  //
  // NOT from a route param, which is what this used to do and got
  // wrong: it read `wsId`, while the shell route declares
  // `/workspaces/:workspaceId`. The param was therefore always
  // undefined in the running app, every link fell back to a flat path,
  // and `WorkspaceRedirect` rebuilds a flat path from its pathname
  // alone — dropping the query string, and with it the `run` and
  // `selected` params these links exist to carry. Only the test router
  // declared `:wsId`, so the tests passed throughout.
  const navigate = useNavigate()
  const wp = useWorkspacePath()
  // #1148: shared themed confirm, replacing native confirm().
  const confirm = useConfirm()
  // #1149: shared themed toast, replacing native alert().
  const toast = useToast()

  const [candidates, setCandidates] = useState<CandidateTest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Id of the candidate the user has requested to delete; null = no
  // dialog open. Hard delete only (#953) — Archive (soft delete) is
  // a separate action and stays as-is on the right-panel header.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null)
  /** The server refused the delete because tests were built from the
   * suggestion. The same dialog then explains and offers Archive. */
  const [deleteRefused, setDeleteRefused] = useState(false)
  // Filters (all in-section, not URL-persisted — the section is
  // nested inside the Candidates tab and won't survive a tab
  // change anyway). Defaults match the Hints page's defaults
  // (status=active, others='all').
  const [filterTag, setFilterTag] = useState<string>('all')
  const [filterSource, setFilterSource] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('active')
  /** #2160 — list order. `priority` reproduces the server's order exactly
   * (P0→P3, newest first inside a band) so it is the default and today's
   * behaviour is unchanged. The other two are client-side over the whole
   * fetched list, which is safe because candidate lists are per-app and
   * small — the same reason `listForApplication` sorts on a computed key
   * without an index. Mirrors the Active list's control. */
  const [sortBy, setSortBy] = useState<string>('priority')

  /** #2158 slice 1 — candidates ticked for creation. A Set because the only
   * questions asked of it are "is this one picked" and "how many", both O(1),
   * and the list can run to hundreds of rows. */
  const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set())
  const [creating, setCreating] = useState(false)
  const [createdRunId, setCreatedRunId] = useState<string | null>(null)
  /** #974 Slice 3 — scripted-status tier filter. `''` is the
   * show-all sentinel matching `TIER_FILTER_OPTIONS`. Filters the
   * candidate list client-side against the per-row tier the parent's
   * `scriptedStatus.byCandidate` map provides. When the snapshot is
   * still in flight (variant just changed), filtering by a specific
   * tier shows nothing — honest, since we don't yet know which row
   * belongs to which tier. */
  const [filterTier, setFilterTier] = useState<string>('')
  const [searchText, setSearchText] = useState('')
  const [busyAction, setBusyAction] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  /** The requirement (document part) a test was proposed for, with how
   * many active tests share it, for the row chip and the detail box. */
  // Counted once per list, not once per row: the list is not paged.
  const activeTestsByPart = useMemo(() => {
    const m = new Map<string, number>()
    for (const o of candidates) {
      if (!o.active || !o.referenceDocId || !o.sourcePartId) continue
      const k = `${o.referenceDocId}:${o.sourcePartId}`
      m.set(k, (m.get(k) ?? 0) + 1)
    }
    return m
  }, [candidates])
  const requirementOf = useCallback((c: CandidateTest) => {
    if (!c.referenceDocId || !c.sourcePartId) return undefined
    const doc = referenceDocs?.find(d => d.id === c.referenceDocId)
    const part = doc?.parts?.find(p => p.id === c.sourcePartId)
    if (!doc || !part) return undefined
    const testCount = activeTestsByPart.get(`${doc.id}:${part.id}`) ?? 0
    return { documentId: doc.id, documentFilename: doc.filename, partId: part.id, title: part.title, quote: part.quote, testCount }
  }, [referenceDocs, activeTestsByPart])

  const docFilenameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const d of referenceDocs ?? []) m.set(d.id, d.filename)
    return m
  }, [referenceDocs])

  /** #1827 — the sources actually present in the current list, in a
   * stable order. Drives the dynamic source filter (chips) below. */
  const presentSources = useMemo(() => distinctCandidateSources(candidates), [candidates])

  const includeInactive = filterStatus !== 'active'

  /** The variant the run settings and picks scope to. Falls back to the
   * first variant when the parent hasn't pinned a selection yet —
   * matches the selector's own `selectedVariantId ?? variants[0]`
   * default so the toolbar and the list agree. */
  const effectiveVariantId = selectedVariantId ?? variants?.[0]?.id

  /** Which list the loaded rows are: active only (`false`) or with
   * archived ones too (`true`). `null` before the first load. Lets a
   * reader tell "the wider list has loaded" from "the filter just
   * changed and its fetch has not started yet", which `loading` alone
   * cannot: for one render after the filter changes, `loading` is still
   * false and the rows are still the old list. */
  const [loadedIncludeInactive, setLoadedIncludeInactive] = useState<boolean | null>(null)

  const loadCandidates = useCallback(async () => {
    setLoading(true)
    try {
      const list = await applicationsAPI.listCandidates(appId, { includeInactive })
      setCandidates(list)
      setLoadedIncludeInactive(includeInactive)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tests')
    } finally {
      setLoading(false)
    }
  }, [appId, includeInactive])

  /** Reload the candidate list AND notify the parent that the active
   * count may have shifted. Use this after any successful mutation
   * (create / archive / hard-delete / re-propose) so the parent's
   * cached `app.candidateTestCount` re-fetches and the Candidate-
   * tests tab badge stays consistent with what the panel shows.
   * Plain `loadCandidates()` (used on mount + on `refreshKey` bumps)
   * doesn't fire the callback because those code paths aren't
   * mutations and the parent's cache is already current.
   * Copilot review on PR #972. */
  /** "Delete all proposed tests" reaches every suggestion for the app,
   * archived included, but the loaded list leaves archived rows out unless
   * the status filter asks for them. So the full count is fetched only
   * when it is needed: on click, for the dialog's title, and when the
   * loaded list is empty, to tell "nothing at all" from "only archived
   * ones". A background fetch on every load would double the list
   * requests for a number that is rarely looked at. */
  const [deleteAllCount, setDeleteAllCount] = useState<number | null>(null)
  const [onlyArchivedLeft, setOnlyArchivedLeft] = useState(false)
  useEffect(() => {
    setOnlyArchivedLeft(false)
    if (loadedIncludeInactive !== false || candidates.length > 0) return
    let cancelled = false
    applicationsAPI.listCandidates(appId, { includeInactive: true })
      .then(list => { if (!cancelled) setOnlyArchivedLeft(list.length > 0) })
      .catch(() => { /* the action just stays hidden */ })
    return () => { cancelled = true }
  }, [appId, candidates, loadedIncludeInactive])
  const offerDeleteAll = Boolean(appName) && (candidates.length > 0 || onlyArchivedLeft)

  async function openDeleteAll() {
    try {
      const all = loadedIncludeInactive
        ? candidates
        : await applicationsAPI.listCandidates(appId, { includeInactive: true })
      setDeleteAllCount(all.length)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't count the proposed tests. Try again.")
    }
  }

  const reloadAfterMutation = useCallback(async () => {
    await loadCandidates()
    // Archiving / restoring / deleting / re-proposing shifts the coverage
    // denominator. The bar reads the parent's status snapshot, so the
    // parent's refresh (fired by this callback) is what updates it.
    onCandidatesMutated?.()
  }, [loadCandidates, onCandidatesMutated])

  useEffect(() => {
    void loadCandidates()
    // `refreshKey` deliberately participates as a dependency so a
    // bump from the parent (proposer just finished against a ref
    // doc) re-runs the loader. `loadCandidates` is itself memoised
    // on `appId` + `includeInactive`, so the dep list change here
    // is the only signal — no double-fetch on key-stable renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCandidates, refreshKey])

  /** Whether the parent's scripted-status snapshot is current for the
   * selected variant. Mirrors the tab-badge stale-snapshot guard from
   * PR #981 — only let the snapshot drive per-row badges, summary
   * counts, and the tier filter when its variantId matches today's
   * selection. During the cold-fetch window after a variant switch,
   * badges hide, summary suppresses tier counts, and the tier filter
   * (if active) suppresses the entire list rather than lying. */
  const scriptedStatusForVariant = useMemo(() => {
    if (!scriptedStatus || !selectedVariantId) return undefined
    if (scriptedStatus.variantId !== selectedVariantId) return undefined
    return scriptedStatus
  }, [scriptedStatus, selectedVariantId])

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    const rows = candidates.filter(c => {
      // Per-document filter (#2096) — the deep link from a reference
      // doc row. Composes with the source-type filter above.
      if (referenceDocId && c.referenceDocId !== referenceDocId) return false
      // One requirement's proposed tests, from a dashboard gap row: a part of
      // a document, or one imported issue.
      if (partId && c.sourcePartId !== partId) return false
      if (issueFilter && (
        c.linkedConnectionId !== issueFilter.connectionId || c.linkedExternalId !== issueFilter.externalId
      )) return false
      if (filterSource !== 'all' && effectiveSource(c) !== filterSource) return false
      if (filterTag !== 'all' && !c.tags.includes(filterTag as CandidateTestTag)) return false
      if (filterStatus === 'active' && !c.active) return false
      if (filterStatus === 'inactive' && c.active) return false
      if (q && !c.name.toLowerCase().includes(q) && !c.description.toLowerCase().includes(q)) return false
      // Scripted-status tier filter — only applies when a tier is
      // selected. Without a current scripted-status snapshot we
      // suppress every candidate rather than show a lying mix.
      // ARCHIVED rows are also suppressed from any tier-specific
      // match: the scripted-status endpoint only returns active
      // candidates, so an archived row's tier is genuinely UNKNOWN —
      // matching it against "Proposed" would be a false positive
      // (PR #984 Copilot review).
      if (filterTier !== '') {
        if (!scriptedStatusForVariant) return false
        if (!c.active) return false
        const tier = scriptedStatusForVariant.byCandidate[c._id] ?? 'proposed'
        if (tier !== filterTier) return false
      }
      return true
    })
    // #2160 — sort AFTER filtering, over a copy: `candidates` is state and
    // `.sort` mutates in place. `priority` is a no-op because the server
    // already ordered the rows that way; choosing another deliberately
    // overrides the priority banding, which is what an explicit sort should
    // do and how the Active list already behaves.
    if (sortBy === 'priority') return rows
    return [...rows].sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      }
      return a.name.localeCompare(b.name)
    })
  }, [candidates, referenceDocId, partId, issueFilter, filterSource, filterTag, filterStatus, filterTier, scriptedStatusForVariant, searchText, sortBy])

  /** #2158 slice 1 — the variant's settings for writing tests, so "Create
   * tests" can start a run without the operator ever choosing one. Creating
   * tests IS a run and a run needs settings, but the operator should never
   * meet that object. Null when the variant has none, which is what
   * disables the button, with an explanation rather than a click that
   * fails. */
  const { byKind: manualTemplates } = useManualRunTemplates(effectiveVariantId)
  const genTemplate = manualTemplates.generation ?? null
  const genTemplateId = genTemplate?.id ?? null

  /** The ceiling in force, or null until the server says (#2286). */
  const maxTestCasesCeiling = useMaxTestCasesPerRun()

  /** How many of the ticked suggestions this run can actually write, or
   * null while the ceiling is unknown.
   *
   * Null is not "all of them" (#2286). The planner clamps server-side,
   * so a button labelled by the raw selection would promise a count the
   * run may not honour — which is the defect #2284 exists to fix. While
   * the number is unknown the button states no count at all. */
  const willCreate = maxTestCasesCeiling === null
    ? null
    : Math.min(picked.size, maxTestCasesCeiling)

  /** How many picks the settings dialog was opened for (#2284). Held
   * separately from `picked` because the selection is cleared once the
   * run starts, and the dialog must not renumber itself mid-flight. */
  const [settingsForPicks, setSettingsForPicks] = useState<string[] | null>(null)

  /** Picks are per-variant: the scripted-status that greys a row out is
   * variant-scoped, so a selection made against one variant means something
   * different against another. Clearing is safer than silently carrying it. */
  useEffect(() => {
    setPicked(new Set())
    // The open settings too (#2284 review). Leaving it up re-renders it
    // against the new variant and its template while `launch` still
    // closes over the previous variant's picked ids, so pressing Run
    // would start the wrong variant's suggestions.
    setSettingsForPicks(null)
  }, [effectiveVariantId])

  /**
   * Turn the ticked candidates into tests (#2158 slice 1).
   *
   * This starts a generation run. The confirmation says so before anything
   * spends, because the operator is one click from a multi-minute run, and
   * the wording deliberately avoids any figure for the cost: it depends on
   * how many iterations each agent takes, so a number here would be a guess
   * presented as a promise.
   *
   * The picks ride the trigger rather than being written onto the template.
   * Patching the template first would have needed no server change at all,
   * and was rejected: a saved template is shared by every future trigger, so
   * accepting a few suggestions this afternoon would silently change what
   * tonight's scheduled run does.
   */
  function handleCreateTests() {
    if (!genTemplateId || picked.size === 0) return
    // #2284 — show the settings rather than a confirm. The confirm said
    // "Create 6 tests?" while the saved template's Max test cases could
    // be 1, so the number on the button and the number governing the run
    // were different and only the first was visible.
    setSettingsForPicks([...picked])
  }

  /** Start the run with what the operator left on screen (#2284).
   *
   * `settings` is undefined when they pressed Save and run, which has
   * already written them to the template; it carries the on-screen
   * values when they pressed Run, which writes nothing. Either way the
   * picks travel here, on the trigger, never on the template. */
  async function startFromSettings(startedIds: string[], settings?: Record<string, unknown>) {
    if (!genTemplateId) return
    const n = startedIds.length
    setCreating(true)
    try {
      const { runId } = await triggerRun(genTemplateId, {
        pickedCandidateIds: startedIds,
        // A named option, not a spread: `triggerRun` reads
        // `settingsOverride`, so top-level keys would be dropped and the
        // run would quietly use the saved template instead.
        ...(settings ? { settingsOverride: settings } : {}),
      })
      setCreatedRunId(runId)
      // Hand the run to the page BEFORE clearing the selection, so the
      // ids survive: `picked` is emptied on the next line and the page
      // needs them to know which rows are being written.
      onRunStarted?.(runId, startedIds)
      setPicked(new Set())
      setSettingsForPicks(null)
      toast({ title: n === 1 ? 'Writing 1 test…' : `Writing ${n} tests…`, variant: 'success' })
    } catch (e) {
      toast({
        title: 'Could not start',
        description: e instanceof Error ? e.message : undefined,
        variant: 'error',
      })
    } finally {
      setCreating(false)
    }
  }

  function togglePicked(id: string) {
    setPicked(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // #1827 — if the active source filter points at a source that is no
  // longer present (its last candidate was archived / deleted), reset
  // to "all" so the list doesn't silently collapse to empty against a
  // chip that has since disappeared.
  useEffect(() => {
    if (filterSource !== 'all' && !presentSources.includes(filterSource)) {
      setFilterSource('all')
    }
  }, [presentSources, filterSource])

  // When the active filter set excludes the currently-selected
  // candidate, clear the right-panel selection so the operator
  // doesn't see a stale detail view.
  //
  // Carve-out for the tier-filter cold-fetch window (Copilot review
  // on PR #984): if a tier filter is active but the snapshot is stale
  // relative to the current variant, `filtered` collapses to empty
  // — and without this carve-out the effect would drop the right-pane
  // selection even though the candidate is likely to reappear the
  // instant the fresh snapshot lands. Treat the suppression as a
  // loading state, not a real exclusion.
  useEffect(() => {
    const tierFilterStaleSuppressing = filterTier !== '' && !scriptedStatusForVariant
    if (tierFilterStaleSuppressing) return
    if (selectedId && !filtered.some(c => c._id === selectedId)) {
      setSelectedId(null)
    }
  }, [filtered, selectedId, filterTier, scriptedStatusForVariant])

  const selected = useMemo(
    () => (selectedId ? candidates.find(c => c._id === selectedId) ?? null : null),
    [candidates, selectedId],
  )

  // Open the suggestion a test's chip asked for. An archived one is not
  // in the default Active list, so the filter widens first and this runs
  // again once the fuller list has loaded. It waits for the list that
  // matches the filter, not just for `loading` to be false: right after
  // the filter widens, the old active-only list is still on screen and
  // would wrongly answer "not found". Filters that could hide the row
  // are cleared, or the selection effect above would drop it.
  useEffect(() => {
    if (!focusCandidateId || loading || loadedIncludeInactive !== includeInactive) return
    const target = candidates.find(c => c._id === focusCandidateId)
    const found = target !== undefined
    if ((!target || !target.active) && filterStatus === 'active') {
      setFilterStatus('all')
      return
    }
    if (found) {
      setFilterTier('')
      setFilterTag('all')
      setFilterSource('all')
      setSearchText('')
      setSelectedId(focusCandidateId)
    }
    onFocusCandidateHandled?.()
  }, [focusCandidateId, loading, loadedIncludeInactive, includeInactive, candidates, filterStatus, onFocusCandidateHandled])

  // ── Action handlers ──────────────────────────────────────

  function openCreate() {
    setEditingId('__new__')
    setDialogOpen(true)
  }

  function openEdit(c: CandidateTest) {
    setEditingId(c._id)
    setDialogOpen(true)
  }

  /** #2126 — inline metadata save from the detail panel. Priority and
   * test type edit in place (save-on-change), so the right panel mutates
   * these two low-risk fields directly rather than through the edit
   * dialog. A light `loadCandidates()` refresh is enough — neither field
   * shifts the active-count or the coverage denominator, so the heavier
   * `reloadAfterMutation` isn't warranted. */
  async function handleSetMeta(
    c: CandidateTest,
    patch: { priority?: TestCasePriority | null; intent?: TestIntent | null },
  ) {
    setBusyAction(c._id)
    try {
      await applicationsAPI.updateCandidate(appId, c._id, patch)
      await loadCandidates()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setBusyAction(null)
    }
  }

  /** Restore an archived suggestion. It counts toward coverage again,
   * and so do the tests linked to it; `reloadAfterMutation` refreshes
   * the parent's status so the bar and counts move without a reload. */
  async function handleRestore(c: CandidateTest) {
    if (c.active) return
    setBusyAction(c._id)
    try {
      await applicationsAPI.updateCandidate(appId, c._id, { active: true })
      await reloadAfterMutation()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Restore failed')
    } finally {
      setBusyAction(null)
    }
  }

  async function handleArchive(c: CandidateTest) {
    if (!c.active) return // already archived
    setBusyAction(c._id)
    try {
      await applicationsAPI.deleteCandidate(appId, c._id)
      await reloadAfterMutation()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Archive failed')
    } finally {
      setBusyAction(null)
    }
  }

  function closeDeleteDialog() {
    setConfirmingDeleteId(null)
    setDeleteRefused(false)
  }

  /** Hard-delete the candidate currently in `confirmingDeleteId`
   * (#953). Distinct from Archive: irreversible, no `active: false`
   * fallback, the row is gone from the collection. Right-panel-only
   * surface — the user has the full candidate context in front of
   * them when they confirm. */
  async function handleDelete() {
    const id = confirmingDeleteId
    if (!id) return
    setBusyAction(id)
    try {
      try {
        await applicationsAPI.deleteCandidate(appId, id, { hard: true })
      } catch (e) {
        if (e instanceof CandidateInUseError) {
          setDeleteRefused(true)
          return
        }
        throw e
      }
      // Drop the selection if the user just deleted the selected
      // row — leaving `selectedId` pointing at a now-gone candidate
      // would render an empty right panel that still claims a
      // selection exists.
      if (selectedId === id) setSelectedId(null)
      setConfirmingDeleteId(null)
      await reloadAfterMutation()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusyAction(null)
    }
  }

  async function handleReproposeAll() {
    const ok = await confirm({
      title: 'Suggest tests again from every document?',
      description:
        'Replaces the tests we suggested from your documents with a fresh set. ' +
        'Proposed tests you edited, added yourself, or built a test from are kept. ' +
        "Proposed tests from Jira aren't affected.",
      confirmLabel: 'Suggest again',
      destructive: true,
    })
    if (!ok) return
    setBusyAction('repropose-all')
    try {
      const result = await applicationsAPI.reproposeAll(appId)
      toast({
        title: `Working on ${result.docsEnqueued} document${result.docsEnqueued === 1 ? '' : 's'}`,
        description: `Removed ${result.deletedExisting} test${result.deletedExisting === 1 ? '' : 's'}. Check back in a minute for the new ones.`,
        variant: 'success',
      })
      await reloadAfterMutation()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't suggest tests. Try again.")
    } finally {
      setBusyAction(null)
    }
  }

  /** Runs from the type-to-confirm dialog, which shows a thrown error in
   * place, so failures are left to throw. Proposed tests a test was built
   * from are kept by the server; the toast says how many. */
  async function handleDeleteAll() {
    const { deleted, kept } = await applicationsAPI.deleteAllCandidates(appId)
    setSelectedId(null)
    setPicked(new Set())
    const testsWord = (n: number) => `${n} proposed test${n === 1 ? '' : 's'}`
    toast(deleted === 0
      ? {
          title: 'Nothing deleted',
          description: kept === 1
            ? 'The one proposed test has tests built from it.'
            : `All ${testsWord(kept)} have tests built from them.`,
        }
      : {
          title: `Deleted ${testsWord(deleted)}`,
          description: kept > 0 ? `Kept ${kept} because tests were built from them.` : undefined,
          variant: 'success',
        })
    await reloadAfterMutation()
  }

  const editingCandidate =
    editingId && editingId !== '__new__'
      ? candidates.find(c => c._id === editingId)
      : undefined

  // ── Render ───────────────────────────────────────────────

  // Sources disclosure (#2261). Starts open only when there are no
  // documents yet — so a new app is still walked through adding the
  // first one; once there is something to collapse, it starts collapsed.
  const sourceDocCount = referenceDocs?.length ?? 0
  const sourcesLabel = sourceDocCount === 0
    ? 'none yet'
    : `${referenceDocs![0]!.filename}${sourceDocCount > 1 ? ` +${sourceDocCount - 1}` : ''}`
  const [sourcesOpen, setSourcesOpen] = useState(openSourcesOnMount || sourceDocCount === 0)
  // referenceDocs can arrive after the first render (the app loads its
  // docs asynchronously), and useState only runs its initializer on
  // mount. Set the disclosure's initial state ONCE from the first real
  // array — an app that already has docs starts collapsed, a new one
  // starts open — without ever overriding a later user toggle (#2261
  // Copilot review).
  const sourcesInitedRef = useRef(false)
  useEffect(() => {
    if (sourcesInitedRef.current || referenceDocs === undefined) return
    sourcesInitedRef.current = true
    setSourcesOpen(openSourcesOnMount || referenceDocs.length === 0)
  }, [referenceDocs, openSourcesOnMount])

  // Compact coverage for the toolbar: scripted + ready over all active
  // suggestions. Read from the one coverage response the dashboard reads,
  // so the bar and the dashboard cannot disagree. Hidden until that
  // response is in for this variant, and when there is nothing to
  // measure.
  const coverageStat = candidates.length > 0 && proposedTests && proposedTests.total > 0
    ? {
        pct: Math.max(0, Math.min(100, proposedTests.scriptedPct)),
        covered: proposedTests.scripted,
        total: proposedTests.total,
        variantName: variants?.find(v => v.id === effectiveVariantId)?.name ?? 'this variant',
      }
    : null

  /** Tests in this variant that count toward no active suggestion. Same
   * snapshot as everything else on the tab. */
  const uncountedCount = scriptedStatusForVariant?.uncountedTestIds?.length ?? 0
  const uncountedVariantName = variants?.find(v => v.id === selectedVariantId)?.name ?? 'this variant'
  /** The sentence that says so, with Show them. Said rather than left
   * out, so a broken link shows up instead of quietly shrinking coverage.
   * Hidden at 0. One element, placed in the summary line when the list is
   * shown and on its own above the empty state when it is not (no active
   * suggestions at all, or the last one archived), so those tests are
   * never left unmentioned. */
  const uncountedNotice = scriptedStatusForVariant && uncountedCount > 0 ? (
    <span
      className="block"
      title="These tests don't count toward coverage. Some came from exploring the app, some were built from a proposed test that was later deleted, and some belong to an archived proposed test."
    >
      {uncountedCount === 1
        ? `1 test in ${uncountedVariantName} doesn't count toward an active proposed test`
        : `${uncountedCount} tests in ${uncountedVariantName} don't count toward an active proposed test`}
      {onShowUncounted && (
        <>
          {' · '}
          <button
            type="button"
            onClick={onShowUncounted}
            className="font-semibold text-primary hover:underline"
          >
            Show them
          </button>
        </>
      )}
    </span>
  ) : null

  return (
    <div className="space-y-3">
      {/* Row 1 — the toolbar: sources summary + a "Manage sources"
          disclosure, the compact coverage stat, and the corpus actions.
          Replaces the old teaching sentence + the full-width coverage
          band (#2261). The two teaching sentences move to the help
          tooltip here and to the embedded manager. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          {sourcesPanel != null && (
            <button
              type="button"
              onClick={() => setSourcesOpen(v => !v)}
              aria-expanded={sourcesOpen}
              className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted whitespace-nowrap"
              title="Add, remove, or edit the documents and links we suggest tests from"
            >
              <span className="text-muted-foreground font-normal">Sources:</span>
              <span className="truncate max-w-[12rem]">{sourcesLabel}</span>
              <span className="text-primary font-semibold">Manage</span>
              {sourcesOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
          )}
          {coverageStat && (
            <>
              <span className="w-px self-stretch bg-border" />
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground/80">
                <span className="text-muted-foreground">Scripted</span>
                <span className="font-semibold tabular-nums">{coverageStat.covered} of {coverageStat.total}</span>
                <span
                  className="inline-block w-14 h-1.5 rounded-full bg-muted overflow-hidden align-middle"
                  role="progressbar"
                  aria-valuenow={coverageStat.pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`Scripted: ${coverageStat.covered} of ${coverageStat.total} proposed tests have a test script in ${coverageStat.variantName} (${coverageStat.pct}%)`}
                >
                  <span className="block h-full bg-primary rounded-full" style={{ width: `${coverageStat.pct}%` }} />
                </span>
                <span className="font-semibold text-primary">{coverageStat.pct}%</span>
                <Tooltip content={TEST_COVERAGE_TOOLTIP} triggerLabel="About Scripted" align="start">
                  <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </Tooltip>
              </span>
            </>
          )}
        </div>
        {/* Toolbar actions as icons that name themselves on hover (#2265),
            matching the per-suggestion row icons and the Active tab. */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Tooltip content="Tests we suggested from your documents, plus any you added yourself. The planner draws on them when it plans a run." triggerLabel="About suggested tests" align="end">
            <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </Tooltip>
          <Button variant="ghost" size="icon-sm" onClick={openCreate} disabled={busyAction !== null} title="Add a Test manually" aria-label="Add a Test manually">
            <Plus className="h-4 w-4" />
          </Button>
          {(referenceDocs?.length ?? 0) > 0 && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleReproposeAll}
              disabled={busyAction !== null}
              title="Suggest again"
              aria-label="Suggest again"
            >
              <RefreshCw className={`h-4 w-4 ${busyAction === 'repropose-all' ? 'animate-spin' : ''}`} />
            </Button>
          )}
          {offerDeleteAll && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="text-destructive hover:text-destructive"
              onClick={() => void openDeleteAll()}
              disabled={busyAction !== null}
              title="Delete all proposed tests"
              aria-label="Delete all proposed tests"
            >
              <DeleteAllIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {deleteAllCount !== null && appName && (
        <ConfirmDeleteDialog
          title={`Delete all ${deleteAllCount} proposed test${deleteAllCount === 1 ? '' : 's'}?`}
          message={`This removes every proposed test for "${appName}". Any that a test was built from are kept, so your coverage stays right. Reading a source again will suggest new ones.`}
          confirmPhrase={appName}
          onConfirm={handleDeleteAll}
          onCancel={() => setDeleteAllCount(null)}
        />
      )}

      {/* The reference-documents manager, embedded and disclosed. */}
      {sourcesPanel != null && sourcesOpen && (
        <div className="rounded-lg border bg-muted/20 p-3">
          {sourcesPanel}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {/* #2158 slice 1 — the run this tab just started. Stays until the
          operator navigates away, so the answer to "did that work?" is on the
          screen they pressed the button on rather than a page they have to go
          find. Links out for the detail instead of re-hosting the run view. */}
      {createdRunId && (
        <div className="border rounded-lg bg-primary/5 border-primary/30 px-3 py-2 flex items-center justify-between gap-3">
          <p className="text-xs">
            Writing your tests. They appear on the <strong>Active</strong> tab as they are finished.
          </p>
          {/* Must open `results?run=<id>`, never `/runs/<id>/report`: that
              route serves the Playwright HTML report, which does not exist
              until the run has finished. The id keeps the page on THIS run
              rather than whichever the active list returns first. */}
          <button
            type="button"
            onClick={() => navigate(wp(`/results?run=${createdRunId}`))}
            className="text-xs font-medium text-primary underline underline-offset-2 shrink-0"
          >
            Open this run
          </button>
        </div>
      )}

      {/* Selection bar. Only present with a selection — an always-on bar
          would be a permanent strip of chrome for an occasional action. */}
      {picked.size > 0 && (
        <div className="border rounded-lg bg-primary/5 border-primary/30 px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-xs font-medium">
            {picked.size === 1 ? '1 selected' : `${picked.size} selected`}
            {maxTestCasesCeiling !== null && willCreate !== null && picked.size > willCreate && (
              // Saying so here rather than letting the extras vanish. The
              // whole point of #2284 is that the number on the button has
              // to be the number the run makes, and a selection above the
              // cap breaks that the other way round. Hidden until the
              // ceiling is known, so it never states a wrong figure.
              <span className="ml-2 font-normal text-muted-foreground">
                a run writes at most {maxTestCasesCeiling} at a time, so {picked.size - willCreate} will wait
              </span>
            )}
          </span>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Visible text, not a `title` on the button. `Button` sets
                `disabled:pointer-events-none`, so a tooltip on a disabled
                control never fires for a mouse and is never announced —
                "disables and says why" would have been half true. */}
            {!genTemplateId && (
              <span className="text-xs text-muted-foreground">
                No saved run settings for this variant yet, so there is nothing to run with.
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={() => setPicked(new Set())} disabled={creating}>
              Clear
            </Button>
            <Button
              size="sm"
              onClick={handleCreateTests}
              disabled={creating || !genTemplateId}
            >
              {creating
                ? 'Starting…'
                : willCreate === null ? 'Create tests'
                : willCreate === 1 ? 'Create test' : `Create ${willCreate} tests`}
            </Button>
          </div>
        </div>
      )}

      {/* With no list to carry the summary line, the uncounted-tests
          sentence stands on its own. */}
      {!loading && candidates.length === 0 && uncountedNotice && (
        <p className="text-xs text-muted-foreground">{uncountedNotice}</p>
      )}

      {/* First-time empty state outside the panel frame — the
          panel chrome (search/filters) is just noise when there
          are no candidates at all + no docs queued.

          Gated on `!error` so an initial-fetch failure shows the
          error banner ALONE rather than the misleading "no
          candidates yet, upload reference documents" onboarding
          message alongside it. Per Copilot review on #878. */}
      {!loading && !error && candidates.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center">
          <Sparkles className="h-5 w-5 mx-auto mb-2 text-muted-foreground/50" />
          <p className="text-sm font-medium">Nothing proposed yet</p>
          <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
            {/* #2158 slice 2 — this used to point at a "Reference docs" tab on
                the Apps page and not link to it, which is advice you cannot
                follow from here. That panel is now directly above, so the
                instruction is a direction rather than a destination. "Candidate
                proposer" also went: it is the name of one of our agents, not
                anything the reader has been taught. */}
            Add a document above that describes what this app should do — a spec, a
            user guide, a set of stories — and we will suggest tests from it. You can
            also <strong>Add manually</strong> to describe one yourself.
          </p>
        </div>
      )}

      {/* Two-panel layout (only mounted when there's at least one
          candidate to show; keeps the empty state clean). Fixed
          height so the inner ResizablePanels has the explicit
          h-full ancestor it needs — the section lives inside the
          Candidates tab, which has no implicit viewport-height
          chain. */}
      {candidates.length > 0 && (
        <div className="border rounded-lg overflow-hidden" style={{ height: 600 }}>
          <ResizablePanels
            defaultWidth={360}
            minWidth={280}
            maxWidth={500}
            left={
              <div className="h-full flex flex-col">
                {/* #974 Slice 2 — variant selector. Scopes every
                    badge / count / summary below it to one variant.
                    Hidden when the app has only one variant; the
                    selection still flows to the parent so the
                    scripted-status fetch keys on the right id. */}
                {variants && variants.length > 1 && (
                  <div className="px-2 pt-2 pb-2 border-b flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Variant</span>
                    <Select
                      options={variants.map(v => ({ value: v.id, label: v.name }))}
                      value={selectedVariantId ?? variants[0]!.id}
                      onChange={e => onVariantChange?.(e.target.value)}
                      className="flex-1 text-xs h-7"
                      aria-label="Variant scope for scripted-status badges"
                    />
                  </div>
                )}
                {/* Filters */}
                <div className="p-2 border-b space-y-2">
                  {/* #2096 — the per-document filter a reference-doc row's
                      View link arrives with. Rendered above the other
                      filters and always visible while active, because it
                      arrived from another screen: without it the user
                      would see a short list with no stated reason.
                      Removable — the question after "what came from this
                      doc?" is usually "what else is proposed?", and a
                      dead-end filter would force a trip back through
                      Apps. Falls back to the id if the document was
                      deleted between authoring the link and following
                      it. */}
                  {issueFilter && (
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="text-muted-foreground">Showing</span>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/35 bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                        From: {candidates.find(c =>
                          c.linkedConnectionId === issueFilter.connectionId
                          && c.linkedExternalId === issueFilter.externalId)?.linkedDisplayKey ?? issueFilter.externalId}
                        {onClearIssueFilter && (
                          <button
                            type="button"
                            onClick={onClearIssueFilter}
                            aria-label="Show proposed tests from every source"
                            className="opacity-70 hover:opacity-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    </div>
                  )}
                  {referenceDocId && (
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                      <span className="text-muted-foreground">Showing</span>
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/35 bg-primary/10 px-2 py-0.5 font-semibold text-primary">
                        From: {docFilenameById.get(referenceDocId) ?? referenceDocId}
                        {partId && (
                          <> › {referenceDocs?.find(d => d.id === referenceDocId)?.parts?.find(p => p.id === partId)?.title ?? 'one part'}</>
                        )}
                        {onClearReferenceDocFilter && (
                          <button
                            type="button"
                            onClick={onClearReferenceDocFilter}
                            aria-label="Show proposed tests from every document"
                            className="opacity-70 hover:opacity-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    </div>
                  )}
                  {/* #1827 — dynamic source filter. One chip per source
                      ACTUALLY present in the list (plus "All"), derived
                      from the rows — never a hardcoded list. Hidden
                      while a single source is present (a filter with one
                      option is just noise); it appears the moment a
                      second source's first candidate lands. */}
                  {presentSources.length > 1 && (
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by source">
                      <button
                        type="button"
                        onClick={() => setFilterSource('all')}
                        aria-pressed={filterSource === 'all'}
                        className={`text-[11px] font-semibold rounded-md border px-2 py-1 transition-colors ${
                          filterSource === 'all'
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-background text-muted-foreground hover:bg-muted/50'
                        }`}
                      >
                        All sources
                      </button>
                      {presentSources.map(key => {
                        const meta = sourceMeta(key)
                        const on = filterSource === key
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => setFilterSource(key)}
                            aria-pressed={on}
                            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-md border px-2 py-1 transition-colors ${
                              on
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border bg-background text-muted-foreground hover:bg-muted/50'
                            }`}
                          >
                            <span
                              className={`inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] text-[7px] font-bold text-white ${meta.markClass}`}
                              aria-hidden="true"
                            >
                              {meta.mark}
                            </span>
                            {meta.label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  <SearchInput
                    value={searchText}
                    onChange={setSearchText}
                    placeholder="Search tests…"
                  />
                  <div className="flex gap-1.5">
                    {/* #2160 — order, alongside the filters rather than in the
                        card header: everything that shapes what the list shows
                        lives in one row. */}
                    <Select
                      options={[...SORT_OPTIONS]}
                      value={sortBy}
                      onChange={e => setSortBy(e.target.value)}
                      className="flex-1 text-xs h-7"
                    />
                    <Select
                      options={[...TAG_OPTIONS]}
                      value={filterTag}
                      onChange={e => setFilterTag(e.target.value)}
                      className="flex-1 text-xs h-7"
                    />
                    <Select
                      options={[...STATUS_OPTIONS]}
                      value={filterStatus}
                      onChange={e => setFilterStatus(e.target.value)}
                      className="flex-1 text-xs h-7"
                    />
                    {/* #974 Slice 3 — Status (scripted-status tier)
                        filter. Extends the existing tag/status row.
                        `''` is the show-all sentinel; selecting a
                        specific tier narrows the visible list. Only
                        shown when scripted-status is wired in (the
                        snapshot the tiers come from): without it the
                        filter has no data and picking a tier would just
                        empty the list, so we hide it entirely (#1827 —
                        the Proposed home doesn't pass a snapshot). */}
                    {scriptedStatus !== undefined && (
                      <Select
                        options={[...TIER_FILTER_OPTIONS]}
                        value={filterTier}
                        onChange={e => setFilterTier(e.target.value)}
                        className="flex-1 text-xs h-7"
                        aria-label="Scripted-status filter"
                      />
                    )}
                  </div>
                  {/* Summary line — names the selected variant + per-
                      tier counts (Slice 4 4-tier scheme) when the
                      scripted-status snapshot is current. Cold-fetch
                      window shows just the bare `X of Y` (matches the
                      tab badge's behavior). Suffix is also suppressed
                      when the visible status filter includes archived
                      rows (`filterStatus !== 'active'`): the per-tier
                      totals are active-only, so showing them alongside
                      an `Showing X of Y` count that mixes active +
                      archived would conflate two scopes (PR #984
                      Copilot review). */}
                  <p className="text-xs text-muted-foreground">
                    Showing {filtered.length} of {candidates.length}
                    {scriptedStatusForVariant && filterStatus === 'active' && (() => {
                      const variantName = variants?.find(v => v.id === selectedVariantId)?.name
                      const { ready, scripted, planned, proposed } = scriptedStatusForVariant.summary
                      return (
                        <>
                          {variantName ? ` in ${variantName}` : ''}
                          {' · '}{ready} ready
                          {' · '}{scripted} scripted
                          {' · '}{planned} planned
                          {' · '}{proposed} proposed
                        </>
                      )
                    })()}
                    {uncountedNotice}
                  </p>
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <p className="text-sm text-muted-foreground p-4">Loading…</p>
                  ) : filtered.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-4">No tests match these filters.</p>
                  ) : (
                    filtered.map(c => {
                      // #974 Slice 3 — per-row scripted-status badge,
                      // 4-tier scheme per Slice 4. Only renders when
                      // the snapshot is current for the selected
                      // variant (cold-fetch window after a variant
                      // switch suppresses the badge rather than show
                      // stale tiers).
                      //
                      // ARCHIVED rows are also suppressed: the
                      // scripted-status endpoint only joins ACTIVE
                      // candidates, so an archived row's tier is
                      // genuinely UNKNOWN — falling back to a tier
                      // label would be a false read (PR #984 Copilot
                      // review). For active rows, the 'proposed'
                      // fallback covers the backfill-miss case where
                      // the candidate exists in the catalog but has
                      // no entry in `byCandidate` (a row missing
                      // from the join has, by construction, no
                      // linked TestCase in this variant).
                      const tier: CandidateScriptedTier | undefined =
                        scriptedStatusForVariant && c.active
                          ? (scriptedStatusForVariant.byCandidate[c._id] ?? 'proposed')
                          : undefined
                      // #2158 slice 1 — a candidate that already has a test in
                      // this variant has nothing to create, so it cannot be
                      // ticked. Same rule the template dialog's picker has
                      // always applied.
                      const alreadyBuilt = tier !== undefined && tier !== 'proposed'
                      // #2260 — a run started from this tab is still writing
                      // this one. The status cannot tell us: until the run's
                      // planner writes the test case, the suggestion honestly
                      // has none, so a correct status reads unbuilt for the
                      // several minutes it takes. Without this the operator
                      // can start a second run for the same suggestion.
                      const beingWritten = writingCandidateIds?.has(c._id) ?? false
                      // #2260 — unknown now blocks instead of allowing. This
                      // used to treat "no snapshot" as selectable, so the
                      // whole feature failing silently (which is exactly what
                      // #1827 caused) read as "nothing has been built yet"
                      // and licensed duplicates. An action we cannot justify
                      // is one we should not offer.
                      const statusUnknown = c.active && scriptedStatusForVariant === undefined
                      const blockedReason = !c.active
                        ? null
                        : beingWritten
                          ? 'We are writing this test now'
                          : alreadyBuilt
                            ? 'This already has a test'
                            : statusUnknown
                              ? "We couldn't check whether this already has a test"
                              : null
                      const isPicked = picked.has(c._id)
                      return (
                        <div
                          key={c._id}
                          className={`w-full flex items-start gap-2 p-3 border-b border-border/50 transition-colors ${
                            isPicked
                              ? 'bg-primary/10 border-l-2 border-l-primary'
                              : selectedId === c._id ? 'bg-primary/10 border-l-2 border-l-primary' : 'hover:bg-muted/50'
                          } ${!c.active ? 'opacity-50' : ''}`}
                        >
                          {/* Outside the row button, not inside it: a nested
                              interactive element inside a button is invalid and
                              would make ticking a row also open it. */}
                          <input
                            type="checkbox"
                            className="mt-1 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                            checked={isPicked}
                            disabled={blockedReason !== null || !c.active}
                            title={blockedReason ?? undefined}
                            onChange={() => togglePicked(c._id)}
                            aria-label={blockedReason
                              ? `${c.name} — ${blockedReason}`
                              : `Select ${c.name}`}
                          />
                        <button
                          onClick={() => setSelectedId(c._id)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {tier !== undefined && (
                              <span
                                className={`text-[10px] shrink-0 px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1 ${TIER_BADGE[tier]}`}
                                aria-label={`Scripted status: ${TIER_LABEL[tier]}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${TIER_DOT[tier]}`} aria-hidden="true" />
                                {TIER_LABEL[tier]}
                              </span>
                            )}
                            {c.priority && (
                              <Badge variant="outline" className={`text-[10px] shrink-0 ${PRIORITY_BADGE[c.priority] || ''}`}>
                                {c.priority}
                              </Badge>
                            )}
                            {c.intent && (
                              <IntentBadge intent={c.intent} className="text-[10px] shrink-0" />
                            )}
                            {c.source === 'user-created' && (
                              <Badge variant="outline" className="text-[10px] shrink-0">user</Badge>
                            )}
                            {c.wasOrphaned && (
                              <Badge variant="outline" className="text-[10px] shrink-0" title="The document this came from was deleted. Your edits were kept.">document deleted</Badge>
                            )}
                            {(() => {
                              const missing = sourceMissingBadge(c)
                              return missing && (
                                <Badge variant="outline" className="text-[10px] shrink-0" title={missing.title}>{missing.label}</Badge>
                              )
                            })()}
                            {c.writeProblem && (
                              <Badge
                                variant="outline"
                                className="text-[10px] shrink-0 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                title="OttoTester couldn't see some steps working, so no test was saved. Open it to see why."
                              >
                                Couldn&apos;t be written
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm font-medium mt-1 line-clamp-2">{c.name}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
                            {c.description}
                          </p>
                          {/* #1827 — provenance: which source produced
                              this candidate + its identifier, so the
                              Proposed list reads the same whatever the
                              source (reference doc today, more later). */}
                          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                            <ProvenanceChip prov={candidateProvenance(c, docFilenameById)} />
                            {(() => {
                              // The requirement this test covers. A label, not a
                              // button: the whole row is already a button.
                              const req = requirementOf(c)
                              return req && (
                                <span
                                  className="inline-flex max-w-full items-center rounded-md border border-primary/35 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary"
                                  title={`Covers the requirement "${req.title}"`}
                                >
                                  <span className="truncate">{req.title}</span>
                                </span>
                              )
                            })()}
                            {alreadyBuilt && (
                              <span className="text-[10px] text-muted-foreground">Already built</span>
                            )}
                            {/* #2160 — when this was suggested. Shares the
                                provenance line rather than taking one of its
                                own, so the row gains a fact without gaining
                                height. "Suggested" not "Created": nothing has
                                been built yet. `title` carries the exact
                                moment, because the relative form collapses
                                everything from one proposer run onto the same
                                label. */}
                            <span
                              className="text-[10px] text-muted-foreground whitespace-nowrap"
                              title={`Suggested ${formatDateTime(c.createdAt, 'absoluteUTC')}`}
                            >
                              Suggested {formatDateTime(c.createdAt, 'relative')}
                            </span>
                          </div>
                        </button>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            }
            right={
              !selected ? (
                <div className="flex items-center justify-center h-full text-sm text-muted-foreground px-6 text-center">
                  Select a test to see its details
                </div>
              ) : (
                <CandidateDetailPanel
                  candidate={selected}
                  requirement={requirementOf(selected)}
                  onFilterByPart={onFilterByPart}
                  sourceFilename={
                    selected.referenceDocId
                      ? docFilenameById.get(selected.referenceDocId)
                      : undefined
                  }
                  // #974 Slice 4 — scripted-as card data. Only
                  // wires through when the snapshot is current for
                  // the selected variant AND the candidate is
                  // active (same suppression rules the per-row
                  // badge uses — archived candidates have unknown
                  // tier, no card). The card itself further hides
                  // when the link list is empty (proposed-tier
                  // candidates have no links).
                  scriptedLinks={
                    scriptedStatusForVariant && selected.active
                      ? scriptedStatusForVariant.linksByCandidate[selected._id]
                      : undefined
                  }
                  scriptedVariantName={
                    scriptedStatusForVariant && selected.active
                      ? variants?.find(v => v.id === selectedVariantId)?.name
                      : undefined
                  }
                  onOpenTest={(testCaseId) => {
                    // `?selected=test-<id>` is the existing
                    // focus param (`useTestsSelection`); `?appId=` +
                    // `?variantId=` are the Slice 4 handoff params
                    // the Tests page consumes on mount to align the
                    // global app + variant context before fetching
                    // plans. Both MUST travel together — passing
                    // only the variant would mis-route when the
                    // operator clicks from an Applications view
                    // that's showing more than one app (e.g.,
                    // visibleApps unfiltered when no global app is
                    // selected), where the Tests page's `useAppContext()
                    // .selectedAppId` could be unset or pointing at
                    // a different app. PR #988 Copilot review.
                    const appParam = `&appId=${encodeURIComponent(appId)}`
                    const variantParam = selectedVariantId
                      ? `&variantId=${encodeURIComponent(selectedVariantId)}`
                      : ''
                    navigate(wp(`/tests?selected=test-${encodeURIComponent(testCaseId)}${appParam}${variantParam}`))
                  }}
                  onEdit={() => openEdit(selected)}
                  // Create test for this one proposed test: the same
                  // settings window the list's Create test opens.
                  // Same rules as ticking it in the list: offered only when
                  // the status is known, it has no test in this variant yet,
                  // and no run from this tab is already writing it.
                  onCreateTest={
                    genTemplateId && selected.active && scriptedStatusForVariant
                      && (scriptedStatusForVariant.byCandidate[selected._id] ?? 'proposed') === 'proposed'
                      && !(writingCandidateIds?.has(selected._id) ?? false)
                      ? () => setSettingsForPicks([selected._id])
                      : undefined
                  }
                  onArchive={() => handleArchive(selected)}
                  onRestore={() => handleRestore(selected)}
                  onDelete={() => setConfirmingDeleteId(selected._id)}
                  onSetPriority={(p) => handleSetMeta(selected, { priority: p })}
                  onSetIntent={(i) => handleSetMeta(selected, { intent: i })}
                  // Disable detail actions whenever ANY action is in
                  // flight — not just this candidate's. A re-propose-
                  // all in progress (busyAction === 'repropose-all')
                  // previously left Edit/Archive/Re-propose-from-doc
                  // clickable; clicking one would overwrite
                  // busyAction mid-operation and allow overlapping
                  // mutations. Per Copilot review on #878.
                  busy={busyAction !== null}
                />
              )
            }
          />
        </div>
      )}

      <CandidateEditDialog
        // Key on the candidate id (or 'new') so re-opening with a
        // different candidate resets the form cleanly.
        key={editingId ?? 'none'}
        appId={appId}
        candidate={editingCandidate}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={reloadAfterMutation}
      />

      {/* The run settings, shown before anything starts (#2284). Same
          dialog the Runs page and the settings list use, so there is one
          editor rather than a second that drifts from it. `launch` swaps
          its footer for Run / Save and run; `pickedFromCandidates`
          prefills Max test cases from the selection and keeps this run's
          picks off the shared template. */}
      {settingsForPicks && genTemplate && effectiveVariantId && (
        <TestAuthoringTemplateDialog
          template={genTemplate}
          allTemplates={[]}
          appId={appId}
          variantId={effectiveVariantId}
          onClose={() => setSettingsForPicks(null)}
          onSaved={() => { /* the run starts from `launch`; nothing to reload here */ }}
          pickedFromCandidates={{ count: settingsForPicks.length }}
          launch={settings => startFromSettings(settingsForPicks, settings)}
        />
      )}

      {/* Hard-delete confirmation (#953). Mirrors the Tests page
          per-test Delete dialog shape (`TestDetail.tsx:679`) — same
          title/body skeleton, same Cancel / Delete (destructive)
          footer. Held closed by `confirmingDeleteId === null`; opens
          when the right-panel Trash button calls
          `setConfirmingDeleteId(selected._id)`. */}
      <Dialog
        open={confirmingDeleteId !== null}
        onOpenChange={(open) => { if (!open) closeDeleteDialog() }}
      >
        <DialogContent className="max-w-sm">
          {deleteRefused ? (
            <>
              <DialogHeader>
                <DialogTitle>This proposed test can&apos;t be deleted</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <p className="text-sm text-muted-foreground">
                  Tests were built from this proposed test. Archive it instead, so its tests stay
                  linked to it.
                </p>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const c = candidates.find(x => x._id === confirmingDeleteId)
                    closeDeleteDialog()
                    if (c) void handleArchive(c)
                  }}
                >
                  Archive
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Delete this test?</DialogTitle>
              </DialogHeader>
              <DialogBody>
                <p className="text-sm text-muted-foreground">
                  Permanently delete{' '}
                  <strong>
                    {`"${candidates.find(c => c._id === confirmingDeleteId)?.name ?? ''}"`}
                  </strong>
                  ? This cannot be undone.
                </p>
              </DialogBody>
              <DialogFooter>
                <Button variant="outline" onClick={closeDeleteDialog}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => { void handleDelete() }}
                  disabled={busyAction === confirmingDeleteId}
                >
                  {busyAction === confirmingDeleteId ? 'Deleting…' : 'Delete'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Right-panel detail view — read-only render of the selected
 * candidate's full content with action buttons that delegate
 * editing to the edit dialog. Same shape as the hints page's
 * right panel: badges header, action buttons, then labeled
 * sections. */
function CandidateDetailPanel({
  candidate,
  requirement,
  onFilterByPart,
  sourceFilename,
  scriptedLinks,
  scriptedVariantName,
  onOpenTest,
  onEdit,
  onCreateTest,
  onArchive,
  onRestore,
  onDelete,
  onSetPriority,
  onSetIntent,
  busy,
}: {
  candidate: CandidateTest
  /** Start writing a test from this proposed test. Absent when there are
   * no settings to write with, or the proposed test is archived. */
  onCreateTest?: () => void
  /** The requirement this test was proposed for, with the document's own
   * words for it. Absent for a test from an issue, or added by hand. */
  requirement?: {
    documentId: string
    documentFilename: string
    partId: string
    title: string
    quote?: string
    testCount: number
  }
  onFilterByPart?: (referenceDocId: string, partId: string) => void
  sourceFilename: string | undefined
  /** #974 Slice 4 — every linked TestCase for this candidate in the
   * currently selected variant. `undefined` means the snapshot
   * isn't current OR the candidate is archived (tier unknown); the
   * card hides entirely. Empty array means the candidate is in the
   * 'proposed' tier (no link yet); the card also hides — there's
   * no link to surface. */
  scriptedLinks?: ReadonlyArray<ScriptedLink>
  /** The variant the links are scoped to. Used for the "in <name>"
   * suffix on the card header so the operator knows which scope
   * the listed tests come from. */
  scriptedVariantName?: string
  /** Click handler for the per-link "Open in Tests" affordance —
   * delegates the navigation back up so the parent can resolve
   * the workspace path + the variant handoff URL params. */
  onOpenTest?: (testCaseId: string) => void
  onEdit: () => void
  onArchive: () => void
  /** Make an archived suggestion active again. */
  onRestore: () => void
  onDelete: () => void
  /** #2126 — inline priority / test-type saves (save-on-change). */
  onSetPriority: (priority: TestCasePriority | null) => void
  onSetIntent: (intent: TestIntent | null) => void
  busy: boolean
}) {
  return (
    <div className="overflow-y-auto h-full p-4 space-y-5">
      {/* Header row: meta badges + action buttons */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {/* #2126 — priority + test type edit inline, save-on-change.
              `allowUnset` keeps a "not specified" option since a candidate's
              priority/intent are nullable (the proposer can leave them
              blank). Disabled while any action is in flight. */}
          <PrioritySelect
            value={candidate.priority}
            onChange={onSetPriority}
            allowUnset
            disabled={busy}
          />
          <IntentSelect
            value={candidate.intent}
            onChange={onSetIntent}
            allowUnset
            disabled={busy}
          />
          <Badge variant="outline" className="text-xs">{candidate.source}</Badge>
          <Badge
            variant="outline"
            className={`text-xs ${candidate.active ? 'text-green-500 border-green-500/30' : 'text-muted-foreground'}`}
          >
            {candidate.active ? 'active' : 'archived'}
          </Badge>
          {candidate.wasOrphaned && (
            <Badge variant="outline" className="text-xs" title="The document this came from was deleted. Your edits were kept.">
              document deleted
            </Badge>
          )}
          {(() => {
            const missing = sourceMissingBadge(candidate)
            return missing && (
              <Badge variant="outline" className="text-xs" title={missing.title}>{missing.label}</Badge>
            )
          })()}
          {/* #2160 — last changed lives here rather than on the row. Scanning
              a list is a "how old is this?" question, which the row's
              suggested-on stamp answers; "has anyone touched it since?" only
              comes up once one candidate is open. Shown only when it differs
              from the suggested date, so an untouched candidate does not
              carry two stamps saying the same thing. */}
          {new Date(candidate.updatedAt).getTime() - new Date(candidate.createdAt).getTime() > 1000 && (
            <span
              className="text-xs text-muted-foreground"
              title={`Last changed ${formatDateTime(candidate.updatedAt, 'absoluteUTC')}`}
            >
              Changed {formatDateTime(candidate.updatedAt, 'relative')}
            </span>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {/* Restore, shown only on an archived suggestion. Archiving takes
              a suggestion and its tests out of coverage; this brings both
              back. */}
          {!candidate.active && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              title="Make this proposed test active again, so its tests count toward coverage."
              onClick={onRestore}
              disabled={busy}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Restore
            </Button>
          )}
          <Button variant="ghost" size="icon-sm" title="Edit" onClick={onEdit} disabled={busy}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            title={candidate.active ? 'Archive' : 'Archived'}
            onClick={onArchive}
            disabled={busy || !candidate.active}
          >
            <Archive className="h-3.5 w-3.5" />
          </Button>
          {/* Hard delete (#953) — irreversible. Lives on the detail
              panel rather than the list row so the user has the
              full candidate context in front of them when they
              confirm. Destructive-tinted icon to differentiate from
              the Archive action visually. */}
          <Button
            variant="ghost"
            size="icon-sm"
            title="Delete permanently"
            onClick={onDelete}
            disabled={busy}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div>
        <h4 className="text-base font-semibold leading-snug">{candidate.name}</h4>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{candidate.description}</p>
      </div>

      {/* Why the last attempt to write a test from this was not saved, in
          the writer's words, with the two ways forward. */}
      {candidate.writeProblem && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-amber-700 dark:text-amber-400">Couldn&apos;t be written</span>
            <span className="text-[11px] text-muted-foreground">{formatDateTime(candidate.writeProblem.at, 'relative')}</span>
          </div>
          <p className="mt-1.5">{candidate.writeProblem.reason}</p>
          {candidate.writeProblem.steps.length > 0 && (
            <ul className="mt-1.5 list-disc pl-5 space-y-0.5">
              {candidate.writeProblem.steps.map((step, i) => <li key={i}>{step}</li>)}
            </ul>
          )}
          <p className="mt-1.5">Change the steps to match what the app does, then create the test again.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onEdit} disabled={busy}>Edit steps</Button>
            {onCreateTest && (
              <Button size="sm" className="h-7 text-xs" onClick={onCreateTest} disabled={busy}>Create test</Button>
            )}
          </div>
        </div>
      )}

      {/* The requirement this test proves, in the document's own words, so
          a reviewer can check the test against them without opening the
          file. */}
      {requirement && (
        <div className="rounded-md border border-primary/35 bg-primary/[0.04] p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-primary">Requirement: {requirement.title}</span>
            {onFilterByPart && (
              <button
                type="button"
                className="text-xs font-medium text-primary underline hover:no-underline"
                onClick={() => onFilterByPart(requirement.documentId, requirement.partId)}
              >
                All {requirement.testCount} {requirement.testCount === 1 ? 'test' : 'tests'} for it
              </button>
            )}
          </div>
          {requirement.quote && (
            <blockquote className="mt-2 rounded-r border-l-2 border-muted-foreground/40 bg-muted/40 px-2.5 py-1.5 text-xs">
              &ldquo;{requirement.quote}&rdquo;
            </blockquote>
          )}
          <p className="mt-1 text-[11px] text-muted-foreground">From {requirement.documentFilename}</p>
        </div>
      )}

      {/* #974 Slice 4 — "Scripted as (in <variant>)" card. Surfaces
          the linked TestCases for this candidate in the scoped
          variant so the operator can navigate to the implementation
          without leaving the candidate's mental context.
          Hides when:
          - `scriptedLinks === undefined` (snapshot stale or
            candidate archived — both suppression rules match the
            per-row badge's),
          - `scriptedLinks.length === 0` (candidate is in the
            'proposed' tier; no linked test to surface).
          More than one linked test is listed plainly. One suggestion
          producing several tests is ordinary — a planner re-run can
          write a variation, and the dedup judge already removes the
          ones that are actually the same. */}
      {scriptedLinks && scriptedLinks.length > 0 && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-muted-foreground">
              Scripted as{scriptedVariantName ? ` (in ${scriptedVariantName})` : ''}
            </Label>
          </div>
          <ul className="space-y-1.5">
            {scriptedLinks.map(link => {
              // Per-link health affordance — green dot for the
              // 'passing' gate (ready-tier evidence), grey dot
              // otherwise. Matches the row-badge dot palette so the
              // visual language is consistent across surfaces.
              const isReady = link.healthStatus === 'passing'
              return (
                <li key={link.testCaseId} className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-1.5 h-1.5 shrink-0 rounded-full ${isReady ? 'bg-green-600 dark:bg-green-400' : 'bg-muted-foreground/60'}`}
                    aria-hidden="true"
                  />
                  <span className="text-sm truncate flex-1" title={link.testCaseName}>
                    {link.testCaseName}
                  </span>
                  {onOpenTest && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 shrink-0 px-2"
                      onClick={() => onOpenTest(link.testCaseId)}
                      title={`Open "${link.testCaseName}" in the Tests page`}
                    >
                      Open in Tests
                      <ArrowUpRight className="h-3 w-3 ml-1" />
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {candidate.tags.length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground">Tags</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {candidate.tags.map(t => (
              <span key={t} className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {candidate.preconditions && candidate.preconditions.length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground">Preconditions</Label>
          <ul className="text-sm mt-1 list-disc pl-5 space-y-0.5">
            {candidate.preconditions.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {candidate.steps && candidate.steps.length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground">Steps</Label>
          <ol className="text-sm mt-1 space-y-2">
            {candidate.steps.map((s, i) => (
              <li key={i} className="leading-relaxed">
                <div className="font-medium">
                  {i + 1}. {s.perform || <em className="font-normal text-muted-foreground">(verify only)</em>}
                </div>
                <ul className="list-disc pl-5 text-muted-foreground text-xs mt-0.5">
                  {s.expect.map((e, j) => (
                    <li key={j}>expect: {e.description}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div>
        <Label className="text-xs text-muted-foreground">Source</Label>
        <p className="text-xs mt-1">
          {/* #1558 Slice 2 — Jira-sourced candidates carry the issue key,
              not a reference-doc filename. Checked first because their
              `source` is still 'proposed'. */}
          {candidate.linkedProvider && (
            <span className="text-muted-foreground">
              from <code className="px-1 bg-muted rounded">{candidate.linkedDisplayKey ?? 'a Jira issue'}</code>
              {candidate.linkedProjectKey ? ` · ${candidate.linkedProjectKey}` : ''}
            </span>
          )}
          {!candidate.linkedProvider && candidate.source === 'user-created' && (
            <span className="text-muted-foreground italic">User-created</span>
          )}
          {!candidate.linkedProvider && candidate.source === 'proposed' && sourceFilename && (
            <span className="text-muted-foreground">from <code className="px-1 bg-muted rounded">{sourceFilename}</code></span>
          )}
          {!candidate.linkedProvider && candidate.source === 'proposed' && !sourceFilename && (
            <span className="text-muted-foreground italic">
              {candidate.wasOrphaned ? 'The document this came from was deleted' : 'Document not found'}
            </span>
          )}
        </p>
      </div>
    </div>
  )
}
